import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';

/**
 * The two themes. There is deliberately no `system` option, which is the one
 * place this differs from FormDesk's store.
 *
 * Light is the default for everyone, on a first visit, whatever the OS says.
 * An accounting screen is read next to paper and printed, and a person who
 * opened the app on a phone set to dark at night and found a black ledger did
 * not choose that. Dark is a choice you make here, and it is remembered.
 */
export type ThemePref = 'light' | 'dark';

export const DEFAULT_THEME: ThemePref = 'light';

/**
 * The localStorage key. Read by the inline script in index.html BEFORE React
 * loads, so the two must agree on the key AND on the value format - see
 * `themeStorage` for why the value is a bare word rather than JSON.
 */
export const THEME_STORAGE_KEY = 'fint-theme';

export const isThemePref = (value: unknown): value is ThemePref =>
  value === 'light' || value === 'dark';

interface ThemeState {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
}

/**
 * Stores the bare word - `dark` or `light` - rather than zustand's default
 * `{"state":{"pref":"dark"},"version":0}` envelope.
 *
 * The consumer that matters is not this store; it is the six-line script in
 * index.html that has to apply the class before first paint and therefore
 * runs before any of this code exists. A bare word it can compare with `===`
 * and never throw on; a JSON envelope it has to parse, and a parse that throws
 * inside a `<head>` script is a light flash for the person it was meant to
 * protect. It also happens to be the format the toggle already wrote before it
 * had a store, so nobody's remembered choice is lost by this change.
 *
 * Anything unrecognised - a value from a future version, a corrupted entry -
 * reads as "no choice made", which is light.
 */
const themeStorage: PersistStorage<Pick<ThemeState, 'pref'>> = {
  getItem: (name) => {
    try {
      const raw = localStorage.getItem(name);
      return isThemePref(raw) ? { state: { pref: raw }, version: 0 } : null;
    } catch {
      // Private mode, or storage disabled. The default stands.
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value.state.pref);
    } catch {
      // The toggle still works for this session; it just will not be remembered.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // Nothing to remove, or nowhere to remove it from. Either is fine.
    }
  },
};

/**
 * Which theme the user asked for.
 *
 * Persisted per browser rather than per user on the server, exactly as in
 * FormDesk: it is a property of the device you are sitting at, not of the
 * account. Someone on a bright office monitor and a phone in the evening
 * wants different answers, and syncing it would fight them.
 *
 * One store rather than component state, because the toggle is mounted twice
 * (desktop sidebar, phone header) and Settings has a third control. Three
 * copies of a `useState` each reading the DOM once at mount is three controls
 * that disagree about which icon to show after any one of them is pressed.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      pref: DEFAULT_THEME,
      setPref: (pref) => set({ pref }),
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: themeStorage,
      partialize: (state) => ({ pref: state.pref }),
    },
  ),
);

/** What the address bar and an installed PWA's title bar are painted. */
export const THEME_COLOR: Record<ThemePref, string> = {
  // --background in each theme, as hex, because <meta theme-color> cannot
  // read a CSS variable.
  light: '#ffffff',
  dark: '#090e1a',
};

/**
 * Put the class on <html> and keep everything that is NOT ours in step.
 *
 * `color-scheme` is the part the class alone cannot do: it tells the browser
 * to draw its own widgets - the scrollbar, a native <select> list, the date
 * picker under <input type="date">, the file chooser - in dark. Without it a
 * dark page opens a white calendar, which reads as a bug rather than a theme.
 *
 * `theme-color` drives the address bar on mobile and the title bar of an
 * installed PWA; left at white it frames a dark page in a bright band.
 *
 * Mirrors the inline script in index.html. If you change what this does, the
 * script has to change with it, or the page paints one way and then the other.
 */
export function applyTheme(pref: ThemePref, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', pref === 'dark');
  root.style.colorScheme = pref;
  const meta = root.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[pref]);
}
